import { Alert, Button, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import Table from 'react-bootstrap/Table';
import { AppAuthStateProps } from '../../types/propTypes';
import { useEffect, useState, useRef } from "react";
import { isDemoUser } from "../../utils/demo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faExclamationTriangle, 
    faRocket, 
    faSpinner, 
    faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import './LLMVisit.css';
import { callWrite, UPDATE_LLM_VISIT_FEEDBACK } from "../../amplify/appsync/write";
import { callQuery, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, PATIENT_VISIT_BY_CSN } from "../../amplify/appsync/query";
import { PatientVisit } from "../../amplify/appsync/search-patients";
import { readBinaryFile, getFileUrl } from "../../amplify/s3/storage";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ReasoningDashboard, REASONING_DASHBOARD_PATH } from './dashboard/ReasoningDashboard';
import { ExplanationDashboard } from './dashboard/ExplanationDashboard';
import { COHORT_ADMIT_TEST_DASHBOARD_PATH, CohortAdmitDashboard } from "./dashboard/CohortAdmitDashboard";
import { CohortRevisitDashboard } from "./dashboard/CohortRevisitDashboard";

// Global constants
const EXPLANATION_DASHBOARD_PATH = 'explanation_dashboard/2025-04-05';
const COHORT_REVISIT_TEST_DASHBOARD_PATH = 'cohort_revisit_test/2025-06-26';

interface DischargeInstructionSurvey {
    completeness: number;
    correctness: number;
    readability: number;
    replaceOriginal: number;
    improveUnderstanding: number;
    formatAcceptability: number;
    followInstructions: number;
    comfortSupervised: number;
    comfortUnsupervised: number;
    mostUsefulAspects: string;
    improvements: string;
    practicalUse: string;
}

interface SurveyStatus {
    isSubmitted: boolean;
    submittedAt?: number;
}

interface SurveyStatuses {
    original: SurveyStatus;
    video: SurveyStatus;
}

interface BaseSurveyQuestions {
    completeness: number;
    correctness: number;
    readability: number;
}

interface OriginalSurvey extends BaseSurveyQuestions {
    type: 'original';
}

interface VideoSurvey extends DischargeInstructionSurvey {
    type: 'video';
    supplementOriginal: number;
}

type SurveyType = OriginalSurvey | VideoSurvey;

interface SurveyFormProps<T extends SurveyType> {
    survey: T;
    setSurvey: (survey: T) => void;
    surveyType: T['type'];
    onSubmit?: (survey: T) => void;
    isSubmitted?: boolean;
    submittedAt?: number;
}

const SurveyForm = <T extends SurveyType>({ 
    survey, 
    setSurvey,
    surveyType,
    onSubmit,
    isSubmitted,
    submittedAt
}: SurveyFormProps<T>) => {
    const [isSaving, setIsSaving] = useState(false);
    const [lastSubmitted, setLastSubmitted] = useState<T | null>(null);

    const handleSubmit = () => {
        setIsSaving(true);
        setLastSubmitted({...survey});
        
        if (onSubmit) {
            onSubmit(survey);
        }
        
        setTimeout(() => {
            setIsSaving(false);
        }, 1000);
    };

    const handleInputChange = (field: string, value: string) => {
        // Check if the field is one of the text fields
        const textFields = ['mostUsefulAspects', 'improvements', 'practicalUse'];
        if (textFields.includes(field)) {
            // Handle text input
            setSurvey({ ...survey, [field]: value });
        } else {
            // Handle numeric input
            const numValue = parseInt(value);
            setSurvey({ ...survey, [field]: numValue });
        }
    };

    const renderScoreRadioGroup = (field: string, label: string) => (
        <Form.Group className="mb-3">
            <Form.Label dangerouslySetInnerHTML={{ __html: label }}></Form.Label>
            <div className="d-flex justify-content-between align-items-center">
                {[1, 2, 3, 4, 5].map((score) => (
                    <div key={score} className="text-center" style={{ flex: 1 }}>
                        <Form.Check
                            type="radio"
                            id={`${field}-${score}`}
                            name={field}
                            value={score}
                            checked={survey[field as keyof T] === score}
                            onChange={(e) => handleInputChange(field, e.target.value)}
                        />
                        <Form.Label 
                            htmlFor={`${field}-${score}`}
                            className="small"
                        >
                            {score}
                        </Form.Label>
                    </div>
                ))}
            </div>
        </Form.Group>
    );

    const hasChanges = lastSubmitted === null || 
        JSON.stringify(lastSubmitted) !== JSON.stringify(survey);

    return (
        <Form className="survey-form">
            {isSubmitted && submittedAt && (
                <Alert variant="success" className="mb-3">
                    <FontAwesomeIcon icon={faCheckCircle} /> Submitted on {new Date(submittedAt).toLocaleString()}
                </Alert>
            )}
            
            <h6>Assessment</h6>
            {/* Base questions for all surveys */}
            {renderScoreRadioGroup('completeness', 'Completeness (1-5)')}
            {renderScoreRadioGroup('correctness', 'Correctness (1-5)')}
            {renderScoreRadioGroup('patient accessibility', 'Patient accessibility (1-5)')}

            {/* Full question set for video survey */}
            {surveyType === 'video' && (
                <>
                    <h6>Video-Specific Questions</h6>
                    <p>Please rate the following questions on a scale of 1-5, where 1=very unlikely, 2=unlikely, 3=maybe, 4=likely, 5=very likely.</p>
                    {renderScoreRadioGroup('improveUnderstanding', 'Do you think the generated video can improve this patient\'s understanding?')}
                    {renderScoreRadioGroup('formatAcceptability', 'Do you think the video format will be more acceptable for patients?')}
                    {renderScoreRadioGroup('followInstructions', 'Do you think this video will better help patients follow the discharge instruction?')}
                    {renderScoreRadioGroup('comfortSupervised', 'Would you feel comfortable walking patients through this video under your supervision?')}
                    {renderScoreRadioGroup('comfortUnsupervised', 'Would you feel comfortable letting patients watch this video by themselves?')}
                    {renderScoreRadioGroup('supplementOriginal', 
                        'Would you consider <u>supplementing</u> the original discharge instruction with this generated instruction?')}
                    {renderScoreRadioGroup('replaceOriginal', 
                        'Would you consider <u>replacing</u> the original discharge instruction with this generated instruction?')}
                    
                    <h6>Open Questions</h6>
                    <Form.Group className="mb-2">
                        <Form.Label>What aspects were most useful or valuable?</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={(survey as VideoSurvey).mostUsefulAspects}
                            onChange={(e) => handleInputChange('mostUsefulAspects', e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>How would you improve it?</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={(survey as VideoSurvey).improvements}
                            onChange={(e) => handleInputChange('improvements', e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>How would you use it in practice?</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={(survey as VideoSurvey).practicalUse}
                            onChange={(e) => handleInputChange('practicalUse', e.target.value)}
                        />
                    </Form.Group>
                </>
            )}

            <div className="d-grid gap-2 mt-4">
                <Button 
                    variant="primary" 
                    onClick={handleSubmit}
                    disabled={isSaving || !hasChanges}
                    className="survey-submit-btn"
                >
                    {isSaving ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                        </>
                    ) : hasChanges ? (
                        'Submit Survey'
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faCheckCircle} /> Submitted
                        </>
                    )}
                </Button>
            </div>
        </Form>
    );
};

export interface LLMVisitProps extends AppAuthStateProps {
    isDashboard?: boolean;
}

export const LLMVisit = (props: LLMVisitProps) => {
    let { searchCSN } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Determine which dashboard is active based on the URL path
    const getDashboardType = (pathname: string): string | null => {
        if (pathname.includes('/explanation')) return 'explanation';
        if (pathname.includes('/reasoning')) return 'reasoning';
        if (pathname.includes('/admit')) return 'admit';
        if (pathname.includes('/revisit')) return 'revisit';
        return null;
    };

    const dashboardType = getDashboardType(location.pathname);
    const isExplanationDashboard = dashboardType === 'explanation';
    const isReasoningDashboard = dashboardType === 'reasoning';
    const isCohortAdmitDashboard = dashboardType === 'admit';
    const isCohortRevisitDashboard = dashboardType === 'revisit';

    // Get the dashboard path based on type
    const getDashboardPath = (type: string | null): string => {
        switch (type) {
            case 'explanation':
                return EXPLANATION_DASHBOARD_PATH;
            case 'reasoning':
                return REASONING_DASHBOARD_PATH;
            case 'admit':
                return COHORT_ADMIT_TEST_DASHBOARD_PATH;
            case 'revisit':
                return COHORT_REVISIT_TEST_DASHBOARD_PATH;
            default:
                return '';
        }
    };

    const [isLoading, setIsLoading] = useState(false);
    const [csn, setCsn] = useState(searchCSN ?? "");
    const [errorMessage, setErrorMessage] = useState("");
    const [outputTarget, setOutputTarget] = useState("discharge_summary");

    const [inputValue, setInputValue] = useState("");
    const [visitData, setVisitData] = useState<any>(null);

    const [summaryLines, setSummaryLines] = useState<any>([]);
    const [csnToUUID, setCsnToUUID] = useState<any>({});

    // Update the state definition
    const [dataCategories, setDataCategories] = useState({
        visit_data: {},
        radiology: [],
        labs: [],
        home_medications: [],
        pmh: [],
        vitals: [],
        medication_orders: [],
        procedure_orders: []
    });
    const [activeDataTab, setActiveDataTab] = useState('visit_data');

    // Function to extract content within <think> tags
    const extractThinkContent = (text: string): string => {
        const thinkMatch = text.match(/<think>([^]*?)<\/think>/);
        return thinkMatch && thinkMatch.length > 1 ? thinkMatch[1] : "";
    };

    const [activeDashboard, setActiveDashboard] = useState<'explanation' | 'reasoning' | null>(null);

    // Update the loadSummaryFile function to use the appropriate dashboard path
    const loadSummaryFile = async () => {
        const summaryPath = `${getDashboardPath(dashboardType)}/summary.txt`;
        console.log("Attempting to fetch summary file from:", summaryPath);

        setErrorMessage("");
        try {
            const summaryData = await readBinaryFile(
                summaryPath,
                props.tenant,
                'ed-llm'
            );
            const decoder = new TextDecoder();
            const summaryText = decoder.decode(summaryData);
                        
            const lines = summaryText.split('\n');
            const summaryLines: any = [];
            const csnToUUID: any = {};
            for (const line of lines) {
                const [lineUuid, lineCSN] = line.split(',');
                summaryLines.push([lineUuid, lineCSN]);
                csnToUUID[lineCSN] = lineUuid;
            }
            setSummaryLines(summaryLines);
            setCsnToUUID(csnToUUID);
        } catch {
            setErrorMessage("Could not load data. Check that you are on the Stanford network and if using VPN that your VPN Group is set to either 'CardinalKey-Full' or 'Stanford-Full'");
        }
    }

    // Update the useEffect to load summary file when the dashboard type changes
    useEffect(() => {
        if (dashboardType) {
            loadSummaryFile();
        }
    }, [dashboardType]);

    const fetchVisitData = async (searchCsn: string) => {
        const decoder = new TextDecoder();
        const uuid = csnToUUID[searchCsn];
        
        try {
            if (!uuid) {
                // Don't show an error if the summary file is still loading
                if (Object.keys(csnToUUID).length === 0) {
                    // Use null instead of error message to indicate loading state
                    setVisitData(null);
                    setErrorMessage("");
                    return;
                }
                
                setVisitData(null);
                setErrorMessage("CSN not found in summary file. Try another CSN.");
                return;
            }

            const dashboardPath = `${getDashboardPath(dashboardType)}/${uuid}.json`;
            console.log("Attempting to fetch data from:", dashboardPath);
            
            const data = await readBinaryFile(
                dashboardPath,
                props.tenant,
                'ed-llm'
            );
            const jsonData = JSON.parse(decoder.decode(data));
            
            setVisitData(jsonData.visit_data);
        } catch (error) {
            console.error("Error fetching data:", error);
            setErrorMessage("Could not load data. Check that you are on the Stanford network and if using VPN that your VPN Group is set to either 'CardinalKey-Full' or 'Stanford-Full'");
        }
    };

    // Update the useEffect to watch for searchCSN changes
    useEffect(() => {
        if (searchCSN) {
            setCsn(searchCSN);
            setInputValue(searchCSN);  // Also update the input field
        }
    }, [searchCSN]);  // Add searchCSN as a dependency

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    };

    // Update the handleSubmit function to use the correct path
    const handleSubmit = () => {
        console.log("Submitted CSN:", inputValue);
        if (inputValue) {
            const basePath = `/llm-visit/${dashboardType}`;
            navigate(`${basePath}/${inputValue}`);
            setCsn(inputValue);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            handleSubmit();
        }
    };

    // Add this effect to handle browser back/forward navigation
    useEffect(() => {
        // If we're at the base URL (/llm-visit) and have no searchCSN,
        // reset the state to show the menu
        if (!searchCSN) {
            setVisitData(null);
            setCsn("");
            setInputValue("");
            setErrorMessage("");
        }
    }, [searchCSN]);

    return (
        <Container style={{ paddingTop: isDemoUser(props.user) ? '143px' : '64px', paddingLeft: '32px', paddingRight: '32px' }} fluid>
            {!isExplanationDashboard && !isReasoningDashboard && !isCohortAdmitDashboard && !isCohortRevisitDashboard ? (
                <Row className="justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                    <Col md={6} className="text-center">
                        <div className="d-grid gap-3">
                            <Button 
                                variant="primary" 
                                size="lg"
                                onClick={() => navigate('/llm-visit/explanation')}
                            >
                                Explanation Dashboard
                            </Button>
                            <Button 
                                variant="primary" 
                                size="lg"
                                onClick={() => navigate('/llm-visit/reasoning')}
                            >
                                Reasoning Dashboard
                            </Button>
                        </div>
                    </Col>
                </Row>
            ) : (
                <>
                    {
                        (isExplanationDashboard || isReasoningDashboard) && (
                            <Row className="mb-3">
                                <Col md={12}>
                                    <InputGroup>
                                        <Button 
                                            variant="outline-secondary"
                                            onClick={() => {
                                                // Reset all relevant states
                                                setVisitData(null);
                                                setCsn("");
                                                setInputValue("");
                                                setErrorMessage("");
                                                // If we have a CSN, we're viewing patient details, so go back to dashboard list
                                                // Otherwise, we're on the dashboard list, so go back to root menu
                                                if (csn) {
                                                    navigate(`/llm-visit/${dashboardType}`);
                                                } else {
                                                    navigate('/llm-visit');
                                                }
                                            }}
                                        >
                                            Back to Menu
                                        </Button>
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={() => {
                                                const currentIndex = summaryLines.findIndex((p: any) => p[1] === csn);
                                                if (currentIndex > 0) {
                                                    const prevCsn = summaryLines[currentIndex - 1][1];
                                                    navigate(`/llm-visit/${dashboardType}/${prevCsn}`);
                                                    setCsn(prevCsn);
                                                }
                                            }}
                                            disabled={!csn || summaryLines.findIndex((p: any) => p[1] === csn) === 0}
                                        >
                                            Previous
                                        </Button>
                                        <Form.Control
                                            placeholder="Search for CSN..."
                                            value={inputValue}
                                            onChange={handleChange}
                                            onKeyDown={handleKeyDown}
                                        />
                                        <Button variant="outline-secondary" onClick={handleSubmit}>
                                            Update
                                        </Button>
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={() => {
                                                const currentIndex = summaryLines.findIndex((p: any) => p[1] === csn);
                                                if (currentIndex < summaryLines.length - 1) {
                                                    const nextCsn = summaryLines[currentIndex + 1][1];
                                                    navigate(`/llm-visit/${dashboardType}/${nextCsn}`);
                                                    setCsn(nextCsn);
                                                }
                                            }}
                                            disabled={!csn || summaryLines.findIndex((p: any) => p[1] === csn) === summaryLines.length - 1}
                                        >
                                            Next
                                        </Button>
                                    </InputGroup>
                                </Col>
                            </Row>
                        )
                    }

                    {isLoading ? (
                        <Row style={{textAlign: 'center'}}>
                            <Col md={12}>
                                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                            </Col>
                        </Row>
                    ) : csn ? (
                        <>
                            {isReasoningDashboard ? (
                                <ReasoningDashboard
                                    csn={csn}
                                    tenant={props.tenant}
                                />
                            ) : isExplanationDashboard ? (
                                <ExplanationDashboard
                                    csn={csn}
                                    tenant={props.tenant}
                                />
                            ) : isCohortAdmitDashboard ? (
                                <CohortAdmitDashboard
                                    csn={csn}
                                    tenant={props.tenant}
                                />
                            ) : isCohortRevisitDashboard ? (
                                <CohortRevisitDashboard
                                    csn={csn}
                                    tenant={props.tenant}
                                />
                            ) : <></>}
                        </>
                    ) : (
                        <>
                            <Row style={{textAlign: 'center'}}>
                                <Col md={12}>
                                    {errorMessage ? (
                                        <Alert variant="danger">
                                            <FontAwesomeIcon icon={faExclamationTriangle} /> {errorMessage}
                                        </Alert>
                                    ) : (
                                        <Alert variant="info">
                                            <FontAwesomeIcon icon={faRocket} /> Look up a CSN above
                                        </Alert>
                                    )}
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <Table size="sm">
                                        <thead>
                                            <tr>
                                                <th>UUID</th>
                                                <th>CSN</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                summaryLines.map((p: any, index: number) => {
                                                    return (
                                                        <tr key={`summary-line-${index}`}>
                                                            <td>{p[0]}</td>
                                                            <td>
                                                                <a href={`/llm-visit/${dashboardType}/${p[1]}`}>
                                                                    {p[1]}
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                        </>
                    )}
                </>
            )}
        </Container>
    );
};
